/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { getSetting, getSettingsByPrefix, listLocalSoundAssets, setSetting, setSettingsBulk } from '../data';

import loginSoundUrl from '../../assets/sounds/login.wav';
import logoutSoundUrl from '../../assets/sounds/logout.wav';
import executorBusySoundUrl from '../../assets/sounds/executor-busy.wav';
import executorIdleSoundUrl from '../../assets/sounds/executor-idle.wav';
import taskPassSoundUrl from '../../assets/sounds/task-pass.wav';
import taskFailSoundUrl from '../../assets/sounds/task-fail.wav';
import wsDownSoundUrl from '../../assets/sounds/ws-down.wav';
import wsUpSoundUrl from '../../assets/sounds/ws-up.wav';
import cashSoundUrl from '../../assets/sounds/cash.wav';

export type SoundEventKey =
    | 'login'
    | 'logout'
    | 'executor_busy'
    | 'executor_idle'
    | 'task_pass'
    | 'task_fail'
    | 'websocket_down'
    | 'websocket_up'
    | 'cost_up';

export interface SoundAssetDef {
    id: string;
    fileName: string;
    label: string;
    url: string;
    local?: boolean;
}

export interface SoundEventDef {
    key: SoundEventKey;
    label: string;
    description: string;
    defaultAssetId: string;
    defaultVolume: number;
    minIntervalMs: number;
}

export interface SoundEventSettings {
    assetId: string;
    volume: number;
    enabled: boolean;
}

export type SoundSettingsMap = Record<SoundEventKey, SoundEventSettings>;

interface PlaySoundOptions {
    ignoreMute?: boolean;
    ignoreEnabled?: boolean;
    assetIdOverride?: string;
    volumeOverride?: number;
    force?: boolean;
}

interface SoundContextValue {
    muted: boolean;
    ready: boolean;
    settings: SoundSettingsMap;
    soundEvents: SoundEventDef[];
    soundAssets: SoundAssetDef[];
    playSound: (key: SoundEventKey, options?: PlaySoundOptions) => Promise<boolean>;
    warmup: () => Promise<boolean>;
    reloadSettings: () => Promise<void>;
    reloadSoundAssets: () => Promise<void>;
    saveSettings: (nextSettings: SoundSettingsMap, nextMuted: boolean) => Promise<void>;
    setMutedPersisted: (nextMuted: boolean) => Promise<void>;
}

const MASTER_MUTE_SETTING_KEY = '/sound/muted';

export const SOUND_ASSETS: SoundAssetDef[] = [
    { id: 'login.wav', fileName: 'login.wav', label: 'Login chime', url: loginSoundUrl },
    { id: 'logout.wav', fileName: 'logout.wav', label: 'Logout close', url: logoutSoundUrl },
    { id: 'executor-busy.wav', fileName: 'executor-busy.wav', label: 'Executor busy', url: executorBusySoundUrl },
    { id: 'executor-idle.wav', fileName: 'executor-idle.wav', label: 'Executor idle', url: executorIdleSoundUrl },
    { id: 'task-pass.wav', fileName: 'task-pass.wav', label: 'Task pass click', url: taskPassSoundUrl },
    { id: 'task-fail.wav', fileName: 'task-fail.wav', label: 'Task fail click', url: taskFailSoundUrl },
    { id: 'ws-down.wav', fileName: 'ws-down.wav', label: 'Socket down alert', url: wsDownSoundUrl },
    { id: 'ws-up.wav', fileName: 'ws-up.wav', label: 'Socket up chirp', url: wsUpSoundUrl },
    { id: 'cash.wav', fileName: 'cash.wav', label: 'Cost cash register', url: cashSoundUrl },
];

let runtimeSoundAssets: SoundAssetDef[] = [ ...SOUND_ASSETS ];
let runtimeSoundAssetMap = Object.fromEntries(runtimeSoundAssets.map((entry) => [entry.id, entry])) as Record<string, SoundAssetDef>;
let runtimeMuted = true;
let runtimeLastPlayed: Record<SoundEventKey, number> = {
    login: 0,
    logout: 0,
    executor_busy: 0,
    executor_idle: 0,
    task_pass: 0,
    task_fail: 0,
    websocket_down: 0,
    websocket_up: 0,
    cost_up: 0,
};

export const SOUND_EVENTS: SoundEventDef[] = [
    {
        key: 'login',
        label: 'Login',
        description: 'Play after a successful sign-in.',
        defaultAssetId: 'login.wav',
        defaultVolume: 0.72,
        minIntervalMs: 1200,
    },
    {
        key: 'logout',
        label: 'Logout',
        description: 'Play when the current session logs out.',
        defaultAssetId: 'logout.wav',
        defaultVolume: 0.7,
        minIntervalMs: 1200,
    },
    {
        key: 'executor_busy',
        label: 'Executor Busy',
        description: 'Play when the executor transitions from idle to active work.',
        defaultAssetId: 'executor-busy.wav',
        defaultVolume: 0.56,
        minIntervalMs: 900,
    },
    {
        key: 'executor_idle',
        label: 'Executor Idle',
        description: 'Play when executor activity drains back to idle.',
        defaultAssetId: 'executor-idle.wav',
        defaultVolume: 0.5,
        minIntervalMs: 900,
    },
    {
        key: 'task_pass',
        label: 'Task Complete: Pass',
        description: 'Play when an individual task finishes successfully.',
        defaultAssetId: 'task-pass.wav',
        defaultVolume: 0.45,
        minIntervalMs: 120,
    },
    {
        key: 'task_fail',
        label: 'Task Complete: Fail',
        description: 'Play when an individual task finishes with failure.',
        defaultAssetId: 'task-fail.wav',
        defaultVolume: 0.55,
        minIntervalMs: 180,
    },
    {
        key: 'websocket_down',
        label: 'WebSocket Down',
        description: 'Play when the live socket disconnects after it had been connected.',
        defaultAssetId: 'ws-down.wav',
        defaultVolume: 0.62,
        minIntervalMs: 1000,
    },
    {
        key: 'websocket_up',
        label: 'WebSocket Up',
        description: 'Play when the live socket reconnects after a disconnect.',
        defaultAssetId: 'ws-up.wav',
        defaultVolume: 0.48,
        minIntervalMs: 1000,
    },
    {
        key: 'cost_up',
        label: 'AI Cost Increased',
        description: 'Play when the live AI total cost increases.',
        defaultAssetId: 'cash.wav',
        defaultVolume: 0.46,
        minIntervalMs: 600,
    },
];

const SOUND_EVENT_MAP = Object.fromEntries(SOUND_EVENTS.map((entry) => [entry.key, entry])) as Record<SoundEventKey, SoundEventDef>;

function setRuntimeSoundAssets(nextAssets: SoundAssetDef[]) {
    runtimeSoundAssets = nextAssets;
    runtimeSoundAssetMap = Object.fromEntries(nextAssets.map((entry) => [entry.id, entry])) as Record<string, SoundAssetDef>;
}

function getEventAssetKey(key: SoundEventKey): string {
    return `/sound/event/${key}`;
}

function getEventVolumeKey(key: SoundEventKey): string {
    return `/sound/volume/${key}`;
}

function getEventEnabledKey(key: SoundEventKey): string {
    return `/sound/enabled/${key}`;
}

function getEventAssetDescription(def: SoundEventDef): string {
    return `Sound source (asset id or URL) mapped to ${def.label.toLowerCase()} event`;
}

function getEventVolumeDescription(def: SoundEventDef): string {
    return `Sound volume for ${def.label.toLowerCase()} event (0.0-1.0)`;
}

function getEventEnabledDescription(def: SoundEventDef): string {
    return `Enable ${def.label.toLowerCase()} sound event (true|false)`;
}

function clampVolume(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(1, Math.max(0, value));
}

function parseBool(value: string | undefined, fallback: boolean): boolean {
    if (typeof value !== 'string') {
        return fallback;
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
        return true;
    }
    if (normalized === 'false') {
        return false;
    }
    return fallback;
}

function parseVolume(value: string | undefined, fallback: number): number {
    const parsed = Number.parseFloat(String(value ?? ''));
    if (Number.isNaN(parsed)) {
        return fallback;
    }
    return clampVolume(parsed);
}

function maybeDecodeUrlLikeValue(value: string): string {
    if (!/%[0-9A-Fa-f]{2}/.test(value)) {
        return value;
    }
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function getRuntimeAssetByFileName(fileName: string): SoundAssetDef | null {
    const normalized = String(fileName || '').trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    return runtimeSoundAssets.find((asset) => asset.fileName.toLowerCase() === normalized) || null;
}

function looksLikeAbsoluteLocalPath(value: string): boolean {
    if (/^[A-Za-z]:[\\/]/.test(value)) {
        return true;
    }
    return /^\/(Users|home|var|tmp|private|opt|Volumes)\//.test(value);
}

function localPathToFileUrl(value: string): string {
    let path = value.replace(/\\/g, '/');
    if (/^[A-Za-z]:\//.test(path)) {
        path = `/${path}`;
    }
    return `file://${encodeURI(path)}`;
}

function normalizeSoundSource(source: string): { cacheKey: string; url: string } {
    const fallback = runtimeSoundAssets[0] || SOUND_ASSETS[0];
    const raw = String(source || '').trim();
    const decoded = maybeDecodeUrlLikeValue(raw).trim();
    const candidate = decoded || raw;

    if (!candidate) {
        return { cacheKey: `asset:${fallback.id}`, url: fallback.url };
    }

    if (runtimeSoundAssetMap[candidate]) {
        return { cacheKey: `asset:${candidate}`, url: runtimeSoundAssetMap[candidate].url };
    }

    const fileNameMatch = candidate.split(/[\\/]/).pop() || candidate;
    const byFileName = getRuntimeAssetByFileName(fileNameMatch);
    if (byFileName) {
        return { cacheKey: `asset:${byFileName.id}`, url: byFileName.url };
    }

    if (/^(https?:|file:|data:|blob:)/i.test(candidate)) {
        return { cacheKey: `url:${candidate}`, url: candidate };
    }

    if (looksLikeAbsoluteLocalPath(candidate)) {
        const fileUrl = localPathToFileUrl(candidate);
        return { cacheKey: `url:${fileUrl}`, url: fileUrl };
    }

    if (/^\//.test(candidate)) {
        return { cacheKey: `url:${candidate}`, url: candidate };
    }

    return { cacheKey: `asset:${fallback.id}`, url: fallback.url };
}

export function getDefaultSoundSettings(): SoundSettingsMap {
    return SOUND_EVENTS.reduce((acc, def) => {
        acc[def.key] = {
            assetId: def.defaultAssetId,
            volume: def.defaultVolume,
            enabled: true,
        };
        return acc;
    }, {} as SoundSettingsMap);
}

class SoundEngine {
    private ctx: AudioContext | null = null;

    private initPromise: Promise<boolean> | null = null;

    private buffers = new Map<string, AudioBuffer>();

    private bufferPromises = new Map<string, Promise<AudioBuffer | null>>();

    private activeNodes = new Set<{ sourceNode: AudioBufferSourceNode; gainNode: GainNode }>();

    async warmup(): Promise<boolean> {
        if (typeof window === 'undefined') {
            return false;
        }
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = (async () => {
            const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!AudioContextCtor) {
                return false;
            }
            if (!this.ctx) {
                this.ctx = new AudioContextCtor();
            }
            if (this.ctx.state === 'suspended') {
                await this.ctx.resume();
            }
            await Promise.all(runtimeSoundAssets.map((asset) => this.loadBuffer(asset.id)));
            return true;
        })().catch((err) => {
            console.error('Failed to initialize sound engine:', err);
            this.initPromise = null;
            return false;
        });
        return this.initPromise;
    }

    private async loadBuffer(source: string): Promise<AudioBuffer | null> {
        const resolved = normalizeSoundSource(source);
        if (this.buffers.has(resolved.cacheKey)) {
            return this.buffers.get(resolved.cacheKey) || null;
        }
        if (this.bufferPromises.has(resolved.cacheKey)) {
            return this.bufferPromises.get(resolved.cacheKey) || null;
        }
        const promise = (async () => {
            if (!this.ctx) {
                return null;
            }
            const response = await fetch(resolved.url);
            const data = await response.arrayBuffer();
            const decoded = await this.ctx.decodeAudioData(data.slice(0));
            this.buffers.set(resolved.cacheKey, decoded);
            return decoded;
        })().catch((err) => {
            console.error(`Failed to load sound source ${source}:`, err);
            return null;
        }).finally(() => {
            this.bufferPromises.delete(resolved.cacheKey);
        });
        this.bufferPromises.set(resolved.cacheKey, promise);
        return promise;
    }

    async play(source: string, volume: number): Promise<boolean> {
        const ready = await this.warmup();
        if (!ready || !this.ctx) {
            return false;
        }

        const buffer = await this.loadBuffer(source);
        if (!buffer) {
            return false;
        }

        const sourceNode = this.ctx.createBufferSource();
        sourceNode.buffer = buffer;

        const gainNode = this.ctx.createGain();
        gainNode.gain.value = clampVolume(volume);

        sourceNode.connect(gainNode);
        gainNode.connect(this.ctx.destination);
        const activeNode = { sourceNode, gainNode };
        this.activeNodes.add(activeNode);

        sourceNode.start(this.ctx.currentTime + 0.005);

        sourceNode.onended = () => {
            this.activeNodes.delete(activeNode);
            sourceNode.disconnect();
            gainNode.disconnect();
        };
        return true;
    }

    stopAll(): void {
        for (const activeNode of this.activeNodes) {
            try {
                activeNode.sourceNode.stop();
            } catch (_error) {
            }
            activeNode.sourceNode.disconnect();
            activeNode.gainNode.disconnect();
        }
        this.activeNodes.clear();
    }
}

const SoundContext = createContext<SoundContextValue | null>(null);

export function SoundManagerProvider({ children }: { children: ReactNode }) {
    const engineRef = useRef<SoundEngine | null>(null);
    const lastPlayedRef = useRef<Record<SoundEventKey, number>>({
        login: 0,
        logout: 0,
        executor_busy: 0,
        executor_idle: 0,
        task_pass: 0,
        task_fail: 0,
        websocket_down: 0,
        websocket_up: 0,
        cost_up: 0,
    });
    const [ready, setReady] = useState(false);
    const [muted, setMuted] = useState(true);
    const [settings, setSettings] = useState<SoundSettingsMap>(() => getDefaultSoundSettings());
    const [soundAssets, setSoundAssets] = useState<SoundAssetDef[]>(() => [ ...SOUND_ASSETS ]);
    const mutedRef = useRef(true);
    const settingsRef = useRef<SoundSettingsMap>(getDefaultSoundSettings());

    if (!engineRef.current) {
        engineRef.current = new SoundEngine();
    }

    useEffect(() => {
        mutedRef.current = muted;
        runtimeMuted = muted;
    }, [muted]);

    useEffect(() => {
        settingsRef.current = settings;
    }, [settings]);

    const warmup = useCallback(async () => {
        const ok = await engineRef.current!.warmup();
        if (ok) {
            setReady(true);
        }
        return ok;
    }, []);

    useEffect(() => {
        let active = true;
        const onGesture = () => {
            void warmup().then((ok) => {
                if (active && ok) {
                    setReady(true);
                }
            });
        };

        window.addEventListener('pointerdown', onGesture, { passive: true });
        window.addEventListener('keydown', onGesture);
        return () => {
            active = false;
            window.removeEventListener('pointerdown', onGesture);
            window.removeEventListener('keydown', onGesture);
        };
    }, [warmup]);

    const reloadSoundAssets = useCallback(async () => {
        const localAssets = await listLocalSoundAssets().catch(() => []);
        const nextAssets = [
            ...SOUND_ASSETS,
            ...localAssets.filter((asset) => asset && asset.id && asset.url),
        ];
        setRuntimeSoundAssets(nextAssets);
        setSoundAssets(nextAssets);
    }, []);

    const reloadSettings = useCallback(async () => {
        const defaults = getDefaultSoundSettings();
        const nextSettings = { ...defaults } as SoundSettingsMap;

        const settingRows = await getSettingsByPrefix('/sound/').catch(() => []);
        const byKey = Object.fromEntries(settingRows.map((row) => [row.key, row.val])) as Record<string, string>;

        for (const def of SOUND_EVENTS) {
            const assetId = byKey[getEventAssetKey(def.key)] ?? def.defaultAssetId;
            const volume = byKey[getEventVolumeKey(def.key)] ?? String(def.defaultVolume);
            const enabled = byKey[getEventEnabledKey(def.key)] ?? 'true';
            const source = String(assetId || '').trim();

            nextSettings[def.key] = {
                assetId: source || def.defaultAssetId,
                volume: parseVolume(volume, def.defaultVolume),
                enabled: parseBool(enabled, true),
            };
        }

        const nextMuted = parseBool(byKey[MASTER_MUTE_SETTING_KEY] ?? 'true', false);

        setSettings(nextSettings);
        setMuted(nextMuted);
        settingsRef.current = nextSettings;
        mutedRef.current = nextMuted;
        runtimeMuted = nextMuted;
    }, []);

    useEffect(() => {
        void reloadSoundAssets();
        void reloadSettings();
    }, [reloadSettings, reloadSoundAssets]);

    const saveSettings = useCallback(async (nextSettings: SoundSettingsMap, nextMuted: boolean) => {
        setSettings(nextSettings);
        setMuted(nextMuted);
        settingsRef.current = nextSettings;
        mutedRef.current = nextMuted;
        runtimeMuted = nextMuted;
        if (nextMuted) {
            engineRef.current?.stopAll();
        }

        const bulkItems = [
            {
                key: MASTER_MUTE_SETTING_KEY,
                value: nextMuted ? 'true' : 'false',
                description: 'Global sound mute toggle (true|false)',
            },
            ...SOUND_EVENTS.flatMap((def) => {
                const value = nextSettings[def.key];
                const source = String(value.assetId || '').trim() || def.defaultAssetId;
                return [
                    { key: getEventAssetKey(def.key), value: source, description: getEventAssetDescription(def) },
                    { key: getEventVolumeKey(def.key), value: value.volume.toFixed(2), description: getEventVolumeDescription(def) },
                    { key: getEventEnabledKey(def.key), value: value.enabled ? 'true' : 'false', description: getEventEnabledDescription(def) },
                ];
            }),
        ];

        await setSettingsBulk(bulkItems);
    }, []);

    const setMutedPersisted = useCallback(async (nextMuted: boolean) => {
        setMuted(nextMuted);
        mutedRef.current = nextMuted;
        runtimeMuted = nextMuted;
        if (nextMuted) {
            engineRef.current?.stopAll();
        }
        await setSetting(MASTER_MUTE_SETTING_KEY, nextMuted ? 'true' : 'false', 'Global sound mute toggle (true|false)');
    }, []);

    const playSound = useCallback(async (key: SoundEventKey, options?: PlaySoundOptions) => {
        const def = SOUND_EVENT_MAP[key];
        const currentSettings = settingsRef.current;
        const setting = currentSettings[key] || {
            assetId: def.defaultAssetId,
            volume: def.defaultVolume,
            enabled: true,
        };

        if (!options?.ignoreEnabled && !setting.enabled) {
            return false;
        }
        if (!options?.ignoreMute && (mutedRef.current || runtimeMuted)) {
            return false;
        }

        const now = Date.now();
        const lastPlayed = Math.max(lastPlayedRef.current[key] || 0, runtimeLastPlayed[key] || 0);
        if (!options?.force && now - lastPlayed < def.minIntervalMs) {
            return false;
        }

        const source = String(options?.assetIdOverride ?? setting.assetId ?? '').trim() || def.defaultAssetId;
        const volume = clampVolume(options?.volumeOverride ?? setting.volume);

        const ok = await engineRef.current!.play(source, volume);
        if (ok) {
            lastPlayedRef.current[key] = now;
            runtimeLastPlayed[key] = now;
            setReady(true);
        }
        return ok;
    }, []);

    const value = useMemo<SoundContextValue>(() => ({
        muted,
        ready,
        settings,
        soundEvents: SOUND_EVENTS,
        soundAssets,
        playSound,
        warmup,
        reloadSettings,
        reloadSoundAssets,
        saveSettings,
        setMutedPersisted,
    }), [muted, ready, settings, soundAssets, playSound, warmup, reloadSettings, reloadSoundAssets, saveSettings, setMutedPersisted]);

    return (
        <SoundContext.Provider value={value}>
            {children}
        </SoundContext.Provider>
    );
}

export function useSoundManager(): SoundContextValue {
    const ctx = useContext(SoundContext);
    if (!ctx) {
        throw new Error('useSoundManager must be used within SoundManagerProvider');
    }
    return ctx;
}